import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import { parseIdentity } from '../../../video-sdk';
import {
  participantsByIdAtom,
  remoteParticipantConnectionStatesByIdAtom,
} from '../../../video-sdk/core/room/participant/atoms';
import { ParticipantConnectionState } from '../../../video-sdk/core/room/participant/types';
import { isMediaShareModeAtom } from '../../state';
import { RemoteParticipantReconnectingOverlayViewModel } from './types';
import { useIsRejoinEnabled as useBaseIsRejoinEnabled } from './useIsRejoinEnabled';
import { useRemoteParticipantReconnectingTrackingEvents as useBaseRemoteParticipantReconnectingTrackingEvents } from './useRemoteParticipantReconnectingTrackingEvents';

export interface UseRemoteParticipantReconnectingOverlayArgs {
  remoteParticipantIdentity: string;
  /**
   * Override the base {@link useBaseIsRejoinEnabled} hook. Useful for testing.
   */
  useIsRejoinEnabled?: typeof useBaseIsRejoinEnabled;
  /**
   * Override the base {@link useBaseRemoteParticipantReconnectingTrackingEvents} hook. Useful for testing.
   */
  useRemoteParticipantReconnectingTrackingEvents?: typeof useBaseRemoteParticipantReconnectingTrackingEvents;
}

interface UseRemoteParticipantReconnectingOverlayResult extends RemoteParticipantReconnectingOverlayViewModel {
  shouldShow: boolean;
}

function getReconnectingOverlayVariation(
  isMediaShareMode: boolean,
  isMultiParticipant: boolean,
): RemoteParticipantReconnectingOverlayViewModel['variation'] {
  if (isMediaShareMode) {
    return 'mini';
  }

  return isMultiParticipant ? 'multi-participant' : 'single-participant';
}

/**
 * View model for the `RemoteParticipantReconnectingOverlay` component.
 *
 * Dispatches Segment events to inform when viewing reconnecting/reconnected
 * remote participants.
 */
export function useRemoteParticipantReconnectingOverlay({
  remoteParticipantIdentity,
  useIsRejoinEnabled = useBaseIsRejoinEnabled,
  useRemoteParticipantReconnectingTrackingEvents = useBaseRemoteParticipantReconnectingTrackingEvents,
}: UseRemoteParticipantReconnectingOverlayArgs): UseRemoteParticipantReconnectingOverlayResult {
  const remoteParticipantsById = useAtomValue(participantsByIdAtom);
  const participantId = parseIdentity(remoteParticipantIdentity).id;
  const connectionState = useAtomValue(remoteParticipantConnectionStatesByIdAtom)[participantId];
  const isReconnecting = connectionState === ParticipantConnectionState.Reconnecting;
  const isMediaShareMode = useAtomValue(isMediaShareModeAtom);
  const isMultiParticipant = Object.keys(remoteParticipantsById).length > 1;
  const [isRejoinEnabled] = useIsRejoinEnabled();
  const prevConnectionStateRef = useRef(connectionState);
  const { trackReconnectedRemoteParticipantViewed, trackReconnectingRemoteParticipantViewed } =
    useRemoteParticipantReconnectingTrackingEvents();

  useEffect(
    function handleViewedSegmentEvents() {
      const prevConnectionState = prevConnectionStateRef.current;

      if (!isRejoinEnabled || prevConnectionState === connectionState) {
        return;
      }

      const { userType, id } = parseIdentity(remoteParticipantIdentity);

      switch (connectionState) {
        case ParticipantConnectionState.Reconnecting:
          trackReconnectingRemoteParticipantViewed(userType, id);
          break;
        case ParticipantConnectionState.Connected:
          if (prevConnectionState === ParticipantConnectionState.Reconnecting) {
            trackReconnectedRemoteParticipantViewed(userType, id);
          }
          break;
        default:
          break;
      }

      prevConnectionStateRef.current = connectionState;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectionState],
  );

  return {
    shouldShow: isReconnecting && isRejoinEnabled,
    variation: getReconnectingOverlayVariation(isMediaShareMode, isMultiParticipant),
    participantDisplayName: remoteParticipantsById[participantId]?.displayName,
  };
}
